import { inject, Injectable } from '@angular/core';
import { SupabaseClientService } from './supabase-client.service';
import { AuthService } from './auth.service';
import { CompanyUser, CompanyUsers, DBUser, OnboardUser } from '../interface/user.interface';
import { Router } from '@angular/router';
import { TripAnalytics, TripRefund, TripsPaginated } from '../interface/trips.interface';
import { Stops } from '../interface/stops.interface';
import { BehaviorSubject, catchError, defer, delay, expand, from, interval, map, Observable, of, retry, switchMap, take } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { ClientCommissionVehicle, ClientCommissionVehicleResponse, NewOwnVehicle, OwnVehicle, Position, RentalVehicle, VehicleDeviceData, VehicleForm, VehiclePaginated, VehiclePosition, VehiclesName } from '../interface/vehicle.interface';
import { SlotForm, SlotPlan } from '../interface/rent.interface';
import { Company, CompanySearchList, CompanyStripe, CompanyToken } from '../interface/company.interface';
import { CompanyLogs, Logs } from '../interface/logs.interface';
import axios from 'axios';
import { Model } from '../interface/model.interface';
import { Devices, DeviceTypeModel } from '../interface/devices.interface';
import { CommissionForm, CommissionRule, VehicleUsingCommission } from '../interface/commission.interface';
import { VehicleProviderAssign } from '../interface/vehicleprovider.interface';
import { WhiteListGroup } from '../interface/whitelist.interface';
import { Voucher } from '../interface/voucher.interface';
import { CookieService } from './cookie.service';
import { Clients, ClientResponse } from '../interface/client.interface';
import { environment } from '../../environments/environment';

const URL_EVOLVO = 'https://api.evolvomobility.com/'
const SUPABASE_APP_URL = environment.SUPABASE_APP_URL;

@Injectable({
  providedIn: 'root'
})
export class SupabaseService {

  currentUser: DBUser | undefined;
  companyData: Company | undefined;
  companyStripeData : CompanyStripe | undefined; 
  stripeAccountData : any | undefined; 
  companyUser: CompanyUser | undefined;
  companyStops : Stops[] | undefined;
  tripAnalytics: TripAnalytics[] | undefined;
  allClients: ClientResponse | undefined;

  totalStations = new BehaviorSubject<Stops[] | null>(null);
  companyPlans = new BehaviorSubject<SlotPlan[]>([]);
  topSixLogs = new BehaviorSubject<CompanyLogs[]>([]);
  companyLogs = new BehaviorSubject<{count:number,logs:CompanyLogs[]} | null>(null);
  companyDataSubject = new BehaviorSubject<Company | null>(null)
  companyStripeDataSubject = new BehaviorSubject<CompanyStripe | null>(null)
  companyTokenSubject = new BehaviorSubject<CompanyToken[] | null>(null);
  devicesSubject = new BehaviorSubject<Devices[] | null>(null)

  supabaseService = inject(SupabaseClientService)
  cookieService = inject(CookieService);
  authService = inject(AuthService)
  router = inject(Router);

  constructor() { }

  async getUser(isRefresh?:string){
    try {
      if(this.currentUser && !isRefresh) return this.currentUser;
      const currentUserSession = await this.authService.getCurrectUserSession();
      if (!currentUserSession?.user?.id) {
        throw new Error("No user session found");
      }
      const {data, error} = await this.supabaseService.client
      .from('user')
      .select('*')
      .eq('user_id',currentUserSession.user.id)
      .single()

      //console.log(data, error);
      if (error) throw error; // Explicitly handle Supabase errors
      if(data) {
        this.currentUser = data;
        return this.currentUser; 
      }
      //If there is no user found we throw to catch block
      throw("User Not Found")
    } catch(error){
      console.log("Error fetching User");
      return null
    }

  }

  async getAllUserCompany():Promise<any[] | 'error'> {
    try {
      const currentUserSession = await this.authService.getCurrectUserSession();
      if(!currentUserSession) return 'error'
      
      const {data, error} = await this.supabaseService.client
      .from('company_user')
      .select(`
        *,
        company(company_name,profile_picture)
        `)
      .eq('user_id',currentUserSession?.user.id)
      .eq('status','active')
      if(error) throw error
      if(!data || data.length === 0) throw 'No company found for user';
      return data
    } catch(err){
      console.log("Error fetching all user company", err);
      return 'error'
    }
  }

  async getCompanyUser():Promise<CompanyUser | null> {
    try {
      if(this.companyUser) return this.companyUser;
      
      //Getting the selected company id from cookie
      const selectedCompanyId = this.cookieService.getCookie('selectedCompanyId');//we are not checking if the cookie exist, because to set the cookie we are using gaurd at dashboard level
      
      const currentUserSession = await this.authService.getCurrectUserSession();
      const {data, error} = await this.supabaseService.client
      .from('company_user')
      .select('*')
      .eq('user_id',currentUserSession?.user.id)
      .eq('company_id',selectedCompanyId)
      .eq('status','active')
      .single()

      if(error) throw error
      this.companyUser = data;
      return data;
    } catch(error){
      console.log("Error fetching User", error);
      return null
    }
  }

  async createUserAndCompany(onboardUser: OnboardUser):Promise<'success' | 'error'> {
    try{
      const {data, error} = await this.supabaseService.client.rpc('create_user_and_company',{
        data: onboardUser
      })
      //console.log(data,error);
      if(error) throw error.message
      this.companyUser = data[0]
      //console.log("Company User at crete user",this.companyUser);
      
      this.insertLog({type:'auth',title:'new account',body:'company account created'})
      this.router.navigate(['/dashboard/home'])
      return 'success'
    }catch(err){
      console.log("Error creating new company/user", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      return 'error';
    }
  }

  async getTripAnalytics():Promise<TripAnalytics[] | []>{
    try{
      if(this.tripAnalytics) return this.tripAnalytics
      const {data, error} = await this.supabaseService.client
      .rpc('trips_analytics',{
        company_id_param:this.companyUser?.company_id
      })
      if(data) return data
      if(error) throw error
      throw new Error('No Trips Found')
    } catch(err){
      console.log("Error fetching trip analytics",err);
      return []
    }
  }

  async getAllComapanyStations(isRefresh?:string){
    try{
      if(this.companyStops && !isRefresh) return this.companyStops
      const {data,error} = await this.supabaseService.client
      .from('stops')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      .order('created_at',{ascending:false})
      if(data) {
        this.companyStops = data;
        this.totalStations.next(data);
        return this.companyStops;
      }
      if(error) throw error
      return [] //need to return in all the code path
    }catch(err){
      console.log("Getting Stops Errror", err);
      return []
    }
  }

  async getAllCompanyBikes(paginationEvent?:PageEvent):Promise<VehiclePaginated | null>{
    try{
      let startIndex = 0;
      let pageSize = 10;
      
      if(paginationEvent) {
        startIndex = paginationEvent.pageIndex * paginationEvent.pageSize;
        pageSize = paginationEvent.pageSize;
      }

      const {data,error} = await this.supabaseService.client
      .rpc('fetch_active_company_vehicles_json',{
        param_company_id:this.companyUser?.company_id,
        limit_num: pageSize,
        offset_num:startIndex
      })      
      if(error) throw error
      if(data) return data
      throw new Error("No Data/error fetching company vehicles")
    } catch(err){
      console.log("Fetching Company Trips Error",err);
      return null
    }
  }

  //REM: REmove this
  async getAllCompanyOwnVehicle(startRange:number, endRange:number):Promise<{vehicleData:OwnVehicle[],totalCount:number} | 'error'>{
    try {
      const {count,data,error} = await this.supabaseService.client
      .from('vehicle')
      .select(`device_id,vehicle_name,serial_no,total_distance,model_id,
        model!inner(vehicle_type,model_name),
        vehicle_owner!inner(),
        vehicle_provider!inner()
        `,{count:'exact',head:false}
      )
      .eq('vehicle_owner.company_id',this.companyUser?.company_id)
      .eq('vehicle_provider.status','active')
      .eq('vehicle_owner.status','active')
      .range(startRange,endRange)
      .order('created_at',{ascending:false})
      //console.log(data,error);
      
      if(error || !count) throw error
      const transformedData = data.map(vehicle => {
        let formatedV;
        // Typescript erro for db relation array and object 
        if(Array.isArray(vehicle.model)) {
          //destructring to single object, for table searching and sorting
          formatedV =  {...vehicle,model_name: vehicle.model[0].model_name,vehicle_type:vehicle.model[0].vehicle_type,imei:null}
        }
        else {
          //destructring to single object, for table searching and sorting
          formatedV = {...vehicle,model_name: vehicle.model['model_name'],vehicle_type:vehicle.model['vehicle_type'],imei:null}
        }
        //remove property model or other
        const {model, ...rest} = formatedV
        return rest //rest will be the remaning property
      })
      //this.OwnVehicleSubject.next({vehicleData:transformedData,totalCount:count})
      return {vehicleData:transformedData,totalCount:count}
    } catch(err) {
      console.log("Error fetching own vehicles", err);
      //this.OwnVehicleSubject.next({vehicleData:[],totalCount:0})
      return 'error'
    }
  }

  async getAllCompanyAssignedVehicles(pageSize:number = 10,startRange:number = 0){
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('company_assigned_own_vehicles',{companyid:this.companyUser?.company_id,_limit:pageSize, _offset:startRange})
      return data
    } catch(err) {
      console.log("Error retriving assigned vehilces",err);
      return 'error'
    }
  }

  async getAllCompanyOwnFreeVehicle(startRange:number, endRange:number):Promise<{vehicleData:OwnVehicle[],totalCount:number} | 'error'>{
    try {
      const limitParam = endRange - startRange + 1;
      const offsetParam = startRange / limitParam;

      const {data,error} = await this.supabaseService.client
      .rpc('vehicle_owner_available_vehicles',{
        companyid:this.companyUser?.company_id,
        limitparam:limitParam, 
        offsetparam: offsetParam
      })
      if(error) throw error

      return data
    } catch(err) {
      console.log("Error fetching own vehicles", err);
      return 'error'
    }
  }

  async getBikesUsingPlan(planId:string):Promise<{vehicle_name:string}[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('vehicle')
      .select('vehicle_name')
      .eq('rental_type_id',planId)
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching plan vehicles", err);
      return []
    }
  }

  async deleteCompanyPlan(planId:string):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('slot_rent')
      .delete()
      .eq('slot_id',planId)
      .single()
      if(error) throw error
      this.getAllCompanyPlans()
      return 'success'
    } catch(err){
      console.log("Error deleting plan", err);
      return 'error'
    }
  }

  async getStopCompanyBike(deviceId:string[]):Promise<VehiclePosition[]>{
    try{
      const {data,error} = await this.supabaseService.client
      .from('vehicle_provider')
      .select('device_id')
      .eq('company_id',this.companyUser?.company_id)
      .in('device_id',deviceId);

      if(error) {
        throw error
      }

      if(data && data.length > 0) {
        const matchingDeviceIds = data.map(device => device.device_id)
        const {data:vehicleData,error:vehicleError} = await this.supabaseService.client
        .from('vehicle') 
        .select(`
          device_id,vehicle_name,
          device_data(position)
          `)
        .in('device_id',matchingDeviceIds)
        if(vehicleError) throw vehicleError
        else {
          //Mapping to new object type instead of giving device_data.position we have 3 parent key as name,id,position
          var mappedVehicles:VehiclePosition[]=[];
          vehicleData.forEach(vehicle => {
            var deviceDataToPosition:Position;
            if(Array.isArray(vehicle.device_data)) deviceDataToPosition = vehicle?.device_data[0].position;
            else deviceDataToPosition = vehicle?.device_data['position'];
            mappedVehicles.push({device_id:vehicle.device_id,vehicle_name:vehicle.vehicle_name,position:deviceDataToPosition})
          })
          return mappedVehicles
        }
      } else {
        return []
      }
    } catch(error){
      console.log("Fetching StopVehicle Error",error);
      return []
    }
  }

  async getVehicleAndDeviceData(deviceId:string): Promise<VehicleDeviceData | null>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('vehicle')
      .select(`
        *,
        device_data(*)
      `)
      .eq('device_id',deviceId)
      .single()
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching vehicle and device data", err);
      return null
    }
  }

  async getVehiclesNameAndId(deviceIds:string[]):Promise<{device_id:string,vehicle_name:string}[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('vehicle')
      .select('device_id,vehicle_name')
      .in('device_id',deviceIds)
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching vehicle name and id", err);
      return []
    }
  }

  async getVehicleModels():Promise<Model[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('model')
      .select('*')
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching vehicle model", err);
      return []
    }
  }

  async getVehiclesWithoutStop(){
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('provider_no_stop_vehilce',{
        param_company_id: this.companyUser?.company_id
      })
      if(error) throw error.message
      return data
    } catch(err) {
      console.log("Error getting vehicles without stops", err);
      return []
    }
  }

  async getUnallocatedDevices():Promise<Devices[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('devices')
      .select(`*,device_data!inner()`)
      .is('device_data.device_id',null)
      .eq('company_id',this.companyUser?.company_id)

      if(error) throw error
      return data
    } catch(err) {
      console.log("Error fetching unallowcated deives", err);
      return []
    }
  }

  async getDeviceData(imei:string):Promise<Devices | null>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('devices')
      .select('*')
      .eq('imei',imei)
      .single()
      return data
    } catch(err){
      console.log("Error fetching selected imei", err);
      return null
    }
  }

  async getCompanyRentalData(rental_type:string):Promise<any[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from(rental_type)
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching rental Data", err);
      return []
    }
  }

  async getAllCompanyPlans():Promise<String>{
    try{
      const {data, error} = await this.supabaseService.client
      .from('slot_rent')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      if(error) throw error
      this.companyPlans.next(data)
      return 'success'
    }catch(err){
      console.log("Error fetching slot_rent",err);
      return 'error'
    }
  }

  /* Trips Section */

  async getAllCompanyTrip(paginationEvent:PageEvent):Promise<TripsPaginated | null>{
    try{
      let startIndex = 0;
      let pageSize = 10;
      
      if(paginationEvent) {
        startIndex = paginationEvent.pageIndex * paginationEvent.pageSize;
        pageSize = paginationEvent.pageSize;
      }

      const {data,error} = await this.supabaseService.client
      .rpc('fetch_company_trips',{
        param_company_id:this.companyUser?.company_id,
        param_limit: pageSize,
        param_offset:startIndex
      })
      if(error) throw error
      if(data) return data
      throw new Error("No Data/error fetching company vehicles")
    } catch(err){
      console.log("Fetching Company Trips Error",err);
      return null
    }
  }

  async forCloseTrip(tripId: string):Promise<'success' | 'error'>{
    try {
      const {data,error} =await this.supabaseService.client
      .functions.invoke(`api/trips/slot_rent/close?tripId=${tripId}`)
      if(error) throw error
      this.insertLog({title:'trip froce closed', type:'operation',body:`Trip #${tripId.split('-')[4]} closed`})
      return 'success'
    } catch(err) {
      console.log("Error froce closing trip",err);
      return 'error'
    }
  }

  async getTripTransferGroup(tripsId:string) : Promise<any[] | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .functions.invoke(`dashApi/stripe/transfers_list?tripId=${tripsId}`)
      if(error) throw error
      return data.data.data
    } catch(err) {
      console.log("Error fetching transfer group",err);
      return 'error'
    }
  }

  async refundForTrip(tripId:string, transferIds:string[],refundFormData:TripRefund): Promise<any | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke('dashApi/stripe/refund_trip',{
        body:{
          tripId:tripId,
          transferIds:transferIds,
          refundData:refundFormData
        }
      })
      if(error) throw error
      this.insertLog({type:'stripe',title:'trip refund submitted',body:`refund of ${refundFormData.amount} processed for trip : #${tripId.split('-')[4]}`})
      return data
    } catch(err) {
      console.log("Error processing refund",err);
      return 'error'
    }
  }

  getRefundData(refundId:string):Observable<any>{
      return from(
        this.supabaseService.client
        .functions
        .invoke(`dashApi/stripe/refunds?refundId=${refundId}`)
        .then((response) => {
          if(response.error) return 'error'
          else return response.data
        })
        .catch(error => {
          console.log("Error fetching refund data",error);
          return 'error'
        })
      )

  }

  /* Trips Section */


  async updateCompanyPlan(planData: SlotForm, slotId:string){
    try {
      const {data, error} = await this.supabaseService.client
      .from('slot_rent')
      .update(planData)
      .eq('slot_id',slotId)
      if(error) throw error
      this.getAllCompanyPlans();
      this.insertLog({type:'operation',title:'plan',body:`updated "${planData.slot_name}"`})
      return 'success' 
    } catch(err){
      console.log("Error updating plan", err);
      return 'error'
    }
  }

  async addCompanyPlan(planData: SlotForm):Promise<'success' | 'error'>{
    try {
      const newPlan = {...planData,company_id:this.companyUser?.company_id}
      const {data, error} = await this.supabaseService.client
      .from('slot_rent')
      .insert(newPlan)
      if(error) throw error
      this.getAllCompanyPlans();
      this.insertLog({type:'operation',title:'plan',body:`created "${planData.slot_name}" plan`})
      return 'success' 
    } catch(err){
      console.log("Error creating plan", err);
      return 'error'
    }
  }

  async getCompanyData(isRefresh?:string){
    try {
      if(!isRefresh && this.companyData) return;
      
      const {data, error} = await this.supabaseService.client
      .from('company')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      .single()
      if(error) throw error
      //console.log("Companuy Data",data,error);
      
      this.companyData = data;
      this.companyDataSubject.next(data)
    } catch(err){
      console.log("Error fetching company data");
    }
  }

  async createStripeAccount():Promise<'success' | 'error'>{
    try {
        const {data, error} = await this.supabaseService.client.functions.invoke('dashApi/stripe/accounts',{
          body:{companyId:this.companyUser?.company_id}
        })
      if(error) throw new Error(error.message)
      this.getCompanyStripeAccount()
      this.insertLog({type:'stripe',title:'payment profile',body:'payment profile created'})
      return 'success'
    } catch(err){
      console.log("Error creating stripe account",err);
      return 'error'
    }
  }

  async getOnboardingLink(useCase:string,returnUrl:string):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .functions
      .invoke('dashApi/stripe/account_links',{
        body:{accountId:this.companyStripeData?.account_id,type:useCase,return_url:returnUrl,refresh_url:`${SUPABASE_APP_URL}/dashboard/account/account_overview`}
      })
      if(error) throw new Error(error.message)
      window.open(data.data.url,"_blank")
      return 'success'
    } catch(err){
      console.log("Error generating onboarding link", err);
      return 'error'
    }
  }

  async updateCompanyData(updateData: any):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('company')
      .update(updateData)
      .eq('company_id',this.companyUser?.company_id)
      if(error) throw error
      this.getCompanyData('refresh')
      return 'success'
    } catch(error){
      console.log("Failed Updating Company Data", error);
      return 'error'
    }
  }

  async updateUserData(updateData: any):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('user')
      .update(updateData)
      .eq('user_id', this.companyUser?.user_id)
      if(error) throw error
      this.getCompanyData('refresh') // pass the data to $companyDataSubject which will pass to subscribers in account component
      this.getUser('refresh') // when changes are made the currentUser still stored with previous data so either we need to make reassign it as undefined to load again from accountOverciew or we load the data from here so it will be availble when account overview is called
      return 'success'
    } catch(error){
      console.log("Failed Updating User Data", error);
      return 'error'
    }
  }

  async getCompanyToken(){
    try {
      //TODO: maintain local copy not to use again and again
      const {data, error} = await this.supabaseService.client
      .from('company_token')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      if(error) throw error
      this.companyTokenSubject.next(data)
    } catch(error){
      console.log("Error fetching company token", error);
    }
  }

  async deleteCompanyToken(token:CompanyToken):Promise<'success' | 'error'>{
    try {
      //TODO: maintain local copy not to use again and again
      const {data, error} = await this.supabaseService.client
      .from('company_token')
      .delete()
      .eq('company_token_id',token.company_token_id)
      if(error) throw error
      this.insertLog({type:'operation',title:'Deleted Token',body:`Deleted, Token - "${token.role}"`})
      this.getCompanyToken()
      return 'success'
    } catch(error){
      console.log("Error fetching company token", error);
      return 'error'
    }
  }

  async generateApiToken(newRole:'admin'|'user'):Promise<'success' | 'error'>{
    try{
      let tokenData = {company_id:this.companyUser?.company_id,role:newRole}
      const currentUserSession = await this.authService.getCurrectUserSession()
      if(!currentUserSession?.access_token) throw new Error('not autherized')
      /* Get JWR  */
      const tokenRes = await axios({
        method:'post',
        url:`${URL_EVOLVO}tools/jwt_generator`,
        headers:{
          "Authorization":`Bearer ${currentUserSession?.access_token}`,
        },
        data: tokenData
      })
      /* Insert JWT to db if the token generated succesfully */
      if(tokenRes.data.access_token){
          return await this.insertTokenToDB(tokenRes.data.access_token,newRole)
      }
      return "error"
    } catch(err){
      console.log("Error generating token");
      return 'error'
    }
  }

  async getPaymentIntents(paymentId:string){
    try {
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke(`api/stripe/payment_intent`,{
        body:{paymentIntentsId: paymentId}
      })
      if(error || data.error) throw error
      return data.data
    } catch(err) {
      console.log("Error fetching payment intents data",err);
      return 'error'
    }
  }

  async insertTokenToDB(token:string, tokenType: 'admin' | 'user'):Promise<'success' | 'error'>{
    try{
      let tokenData = {company_id:this.companyUser?.company_id,role:tokenType,access_token:token}
      const {data, error} = await this.supabaseService.client
      .from('company_token')
      .insert(tokenData)
      if(error) throw error
      this.insertLog({type:'operation',title:'Created New Token',body:`Created new token - "${tokenType}"`})
      this.getCompanyToken()
      return 'success'
    } catch(err){
      console.log("Error inserting token",err);
      return 'error'
    }
  }

  async unlockTlkVehicle(deviceId:string):Promise<'unlocked'>{
    try {
      const currentUserSession = await this.authService.getCurrectUserSession()
      if(!currentUserSession?.access_token) throw new Error('not autherized')
      await axios({
        method:'POST',
        url:URL_EVOLVO+'devices/tlk/unlock/'+deviceId,
        headers:{
          "Authorization":`Bearer ${currentUserSession.access_token}`
        }
      })
      return 'unlocked'
    } catch(err){
      throw err
    }
  }

  unlockVehilce(deviceId: string) {
    return defer(()=>from(this.unlockTlkVehicle(deviceId))).pipe(
      retry(1),//retry once on error
      catchError(err => of('failed ' + (err.status ?? '00')))
    );
  }

  async addNewVehicle(vehicleData:NewOwnVehicle):Promise<'success' | 'error'>{
    try {
      //Update COmpany Id with data
      const vehicleDataWithCompanyId = {...vehicleData,company_id:this.companyUser?.company_id}
      
      const {data,error} = await this.supabaseService.client
      .rpc('add_vehicle',{newvehicledata:vehicleDataWithCompanyId})
      if(error) throw error
      //console.log(data);
      this.getAllCompanyOwnVehicle(0,9)
      return 'success'
    } catch(error){
      console.log("Error adding new Vehicle",error);
      return 'error'
    }
  }
  
  async addNewRentalVehicle(bikeFormData:RentalVehicle){
    try {
      const {data, error} = await this.supabaseService.client
      .rpc('add_direct_rental_vehicle',{
        company_id_param: this.companyUser?.company_id,
        vehicle_name_param: bikeFormData.vehicle_name,
        serial_no_param: bikeFormData.serial_no,
        model_id_param:bikeFormData.model_id,
        imei_param: bikeFormData.imei,
        rental_type_param: bikeFormData.rental_type,
        rental_type_id_param: bikeFormData.rental_type_id,
        commission_id_param: bikeFormData.commission_id,
        expire_at_param: bikeFormData.expire_at
      })
      console.log(data,error);
      if(error) return {status:'error',data:error.message ?? 'Unknown error processing request'}
      return {status:'success',data:data}
    } catch(err){
      console.log("Failed adding rental bike",err);
      return {status:'error',data:err?.toString()}
    }
  }

  async updateOwnVehicle(vehicleData:OwnVehicle,device_id:string):Promise<'success' | 'error'>{
    try {
      const {vehicle_name,serial_no,model_id,imei} = vehicleData
      let updateVehicleData = {p_vehicle_name:vehicle_name,p_serial_no:serial_no,p_model_id:model_id,p_device_id:device_id,p_imei:imei}

      const {data,error} = await this.supabaseService.client
      .rpc('update_own_vehicle',updateVehicleData)
      if(error) throw error
      //this.getAllCompanyOwnFreeVehicle(0,9)
      this.refreshDeviceListOnServer()
      return 'success'
    } catch(err){
      console.log("Error updating vehicle",err);
      return 'error'
    }
  }

  async refreshDeviceListOnServer(){
    try {
      const updateRes = await axios({
        method : 'POST',
        url:`${URL_EVOLVO}tools/list_update`
      })
    } catch(err) {
      console.log("Error updating list", err);
    }
  }

  async deleteOwnVehicle(vehicle: OwnVehicle) {
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('soft_delete_vehicle',{deviceid:vehicle.device_id})
      if(error) throw error
      this.insertLog({type:'operation',title:'delete vehicle',body:`vehicle ${vehicle.vehicle_name} deleted`})
      return 'success'
    } catch(err) {
      console.log("Error deleting vehicle", err);
      return 'error'
    }
  }

  async updateProviderVehicle(vehicleData:VehicleForm,device_id:string):Promise<'success' | 'error'>{
    try {
      const {vehicle_name,rental_type,rental_type_id,imei} = vehicleData
      let updateVehicleData = {p_vehicle_name:vehicle_name,p_rental_type:rental_type,p_rental_type_id:rental_type_id,p_device_id:device_id,p_imei:imei}

      const {data,error} = await this.supabaseService.client
      .rpc('update_vehicle',updateVehicleData)
      if(error) throw error
      this.refreshDeviceListOnServer()
      return 'success'
    } catch(err){
      console.log("Error updating vehicle",err);
      return 'error'
    }
  }

  async updateCompanyStop(stopData:any,stopId:string):Promise<'success' | 'error'>{
    try {
      const {error} = await this.supabaseService.client
      .from('stops')
      .update(stopData)
      .eq('company_id',this.companyUser?.company_id)
      .eq('stop_id',stopId)
      .single()
      if(error) throw error
      this.getAllComapanyStations('refresh')
      this.insertLog({type:'operation',body:`updated "${stopData.name}" stop`,title:'updated stop'})
      return 'success'
    } catch(err){
      console.log("Error Updating stop", err);
      return 'error'
    }
  }

  async deleteCompanyStop(stop:Stops): Promise <'success' | 'error'>{
    try {
      const {error} = await this.supabaseService.client
      .from('stops')
      .delete()
      .eq('stop_id',stop.stop_id)
      .eq('company_id', this.companyUser?.company_id)
      .single()
      if(error) throw error
      this.getAllComapanyStations('refresh')
      this.insertLog({type:'operation', title:'stop deleted', body:`stop "${stop.name}" deleted`})
      return 'success'
    } catch(err){
      console.log("Error deleting stop");
     return 'error' 
    }
  }

  async createStop(stopData:any):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('stops')
      .insert({...stopData,company_id: this.companyUser?.company_id})
      if(error) throw error
      this.getAllComapanyStations('refresh')
      this.insertLog({type:'operation', title:'created stop', body:`new stop "${stopData.name}" created`})
      return 'success'
    } catch(err){
      console.log("Error creating new stop", err);
      return 'error'
    }
  }

  async getUnallocatedCompanyVehicles():Promise<{device_id:string,vehicle_name:string}[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .rpc('company_unallocated_vehicles',
        {param_company_id: this.companyUser?.company_id}
      )
      //console.log(data,error);
      
      if(error) throw error
      return data
    } catch(error){
      console.log("Error fetching unallocated vehicles", error);
      return []
    }
  }

  async getCompanyListForSearch(comapnySearchInput:string):Promise<CompanySearchList[]>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company')
      .select(`
        company_id,company_name,profile_picture,status,
        company_stripe(onboarding_status)
        `)
      .ilike('company_name',`%${comapnySearchInput}%`)
      
      if(error) throw error

      const formatedCompanyLists: CompanySearchList[] = data.map(company => {

        // Destructure `company_stripe` out and keep the rest
        const {company_stripe, ...companyWithoutStripe} = company

        if(!Array.isArray(company_stripe) && company_stripe !== null) {
          return {...companyWithoutStripe,stripe_account_status:company_stripe['onboarding_status']}
        } else {
          return {...companyWithoutStripe,stripe_account_status:'pending'}
        }
      })
      return formatedCompanyLists
    } catch(err){
      console.log("Error fetching company lists",err);
      return []
    }
  }

  async getOtherCompanyLimitedData(companyId:string):Promise<CompanySearchList | null>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company')
      .select(`
        company_id,company_name,profile_picture,status,
        company_stripe(onboarding_status)
        `)
      .eq('company_id',companyId)
      .single()
      if(error) throw error

      let formatedCompany: CompanySearchList | null = null
      
      // Destructure `company_stripe` out and keep the rest
      const {company_stripe, ...companyWithoutStripe} = data
      if(!Array.isArray(company_stripe) && company_stripe !== null) {
        formatedCompany = {...companyWithoutStripe,stripe_account_status:company_stripe['onboarding_status']}
      } else {
        formatedCompany = {...companyWithoutStripe,stripe_account_status:'pending'}
      }
      return formatedCompany
    } catch(err){
      console.log("Error fetching company lists",err);
      return null
    }
  }

  async getCompanyCommissionList():Promise<CommissionRule[]>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('commission_rules')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      .order('created_at',{ascending:false})
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching company commission",err);
      return []
    }
  }

  async assignVehiclesToCompany(vehicleData:VehicleProviderAssign,companyName:string | undefined):Promise<'success' | 'error'>{
    try {
      const {company_id,device_id,commission_id,expire_at} = vehicleData
      const formatedVehicleData = {p_company_id:company_id,p_device_id:device_id,p_commission_id:commission_id,p_expire_at:expire_at}
      //console.log(formatedVehicleData);
      
      const {data,error} = await this.supabaseService.client
      .rpc('assign_vehicle_to_company',formatedVehicleData)
      if(error) throw error
      this.insertLog({type:'operation',title:'assign vehicle to company',body:`vehicle #${device_id[0].split('-')[4]} and other assigned to ${companyName || 'company'}`})
      return 'success'
    } catch(err) {
      console.log("Error assigning vehilces to company",err);
      return 'error'
    }
  }

  async reclaimProviderVehicle(deviceIds: string[],companyId:string):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('reclaim_provider_vehicle',{device_id_param:deviceIds,company_id_param: companyId})
      if(error) throw error
      this.insertLog({type:'operation',title:'reclaim vehicle initiated',body:`${deviceIds.length} vehicles reclaimed to your pool`})
      return 'success'
    } catch(err) {
      console.log("Error reclaming selected vehilce", err);
      return 'error'
    }
  }

  async extendProviderVehicle(deviceIds: string[],companyData:{companyId:string,companyName:string},extendDate:string, commissionId:string | null):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('extend_provider_vehicle',{device_id_param: deviceIds,owner_id_param:this.companyUser?.company_id,provider_id_param:companyData.companyId,extend_date_param:extendDate,commission_id_param:commissionId})
      if(error) throw error
      this.insertLog({type:'operation',title:'extend vehicle contract initiated',body:`${deviceIds.length} vehicles of ${companyData.companyName} contract were extended`})
      return 'success'
    } catch(err){
      console.log("Error estending provider vehicle", err);
      return 'error'
    }
  }

  /* Company Stripe Section */

  async getCompanyStripe():Promise<CompanyStripe | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_stripe')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      .single()
      if(error) throw error
      return data
    } catch(err){
      console.log("Error fetching company stripe details");
      return 'error'
    }
  }

  async getCompanyStripeAccount(isRefresh?:string){
    try {
      if(!isRefresh && this.companyStripeData) return;
      const {data, error} = await this.supabaseService.client
      .from('company_stripe')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      if(error || !data.length) throw error
      this.companyStripeData = data[0];
      this.companyStripeDataSubject.next(data[0])
      return data[0] //return is used in resolver
    } catch(err){
      console.log("Error fetching company stripe data",err);
      return 'error' //return is used in resolver
    }
  }

  async getStripeData(isRefresh?:string):Promise<any | 'error'>{
    try {
      if(!isRefresh && this.stripeAccountData) return this.stripeAccountData

      const {data, error} = await this.supabaseService.client.functions.invoke('dashApi/stripe/accounts/account',{
        body:{accountId:this.companyStripeData?.account_id}
      })
      if(error) throw new Error(error.message)
      this.stripeAccountData = data.data
      return this.stripeAccountData
    } catch(err){
      console.log("Error creating stripe account",err);
      return null
    }
  }

  async updateCompanyStripe(accountStatus:string):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_stripe')
      .update({onboarding_status:accountStatus})
      .eq('company_id',this.companyUser?.company_id)
      .single()
      if(error) throw error
      return 'success'
    } catch (err) {
      console.log("Error updating stripe status", err);
      return 'error'
    }
  }

  /* Company Stripe Section */

  /* Vocher Section */
  
  async getVocher(startRange:number = 0,endRange:number = 10){
    try {
      const {count,data,error} = await this.supabaseService.client
      .from('voucher')
      .select('*',{count:'exact',head:false})
      .eq('company_id',this.companyUser?.company_id)
      .gte('ends_at',new Date().toISOString())
      .order('created_at',{ascending:false})
      .range(startRange,endRange)
      if(error) throw error
      return {data:data,totalCount:count}
    } catch(err) {
      console.log("Error fetching voucher",err);
      return 'error'
    }
  }

  async addVoucher(voucherData: any){
    try {
      const voucherWithCompanyId = {...voucherData, company_id:this.companyUser?.company_id}

      const {data,error} = await this.supabaseService.client
      .from('voucher')
      .insert(voucherWithCompanyId)
      if(error) throw error
      this.insertLog({type:'operation',title:'created new voucher',body:`new voucher ${voucherData.name} created`})
      return 'success'
    } catch(err) {
      console.log("Error inserting voucher", err);
      return 'error'
    }
  }

  async updateVoucher(voucherData: any,voucherId:string){
    try {
      const {data,error} = await this.supabaseService.client
      .from('voucher')
      .update(voucherData)
      .eq('voucher_id',voucherId)
      if(error) throw error
      this.insertLog({type:'operation',title:'voucher updated',body:`voucher ${voucherData.name} updated`})
      return 'success'
    } catch(err) {
      console.log("Error updating vouhcer", err);
      return 'error'
    }
  }

  async deleteVoucher(voucher:Voucher){
    try {
      const {data,error} = await this.supabaseService.client
      .from('voucher')
      .delete()
      .eq('voucher_id', voucher.voucher_id)
      .single()
      if(error) throw error
      this.insertLog({type:'operation',title:'deleted voucher',body:`vouhcer ${voucher.name} deleted`})
      return 'success'
    } catch(err) {
      console.log("Error deleting voucher", err);
      return 'error'
    }
  }

  /* Vocher Section */

  /* Whitelist Groups */

  async getWhiteListGroup(startRange:number = 0,endRange:number = 10){
    try {
      const {count,data,error} = await this.supabaseService.client
      .from('whitelist_groups')
      .select(`*,voucher(name,discount_percentage)`,{count:'exact',head:false})
      .eq('company_id',this.companyUser?.company_id)
      .range(startRange,endRange)
      .order('created_at',{ascending:false})
      if(error)  throw error
      return {totalCount:count,data:data}
    } catch(err) {
      console.log("Error fetching whitelist groups",err);
      return 'error'
    }
  }

  async addWhiteGroup(whiteListGroup: any){
    try {
      const groupWithCompanyId = {...whiteListGroup, company_id:this.companyUser?.company_id}

      const {data,error} = await this.supabaseService.client
      .from('whitelist_groups')
      .insert(groupWithCompanyId)
      if(error) throw error
      this.insertLog({type:'operation',title:'added new whitlist group',body:`whitelist group ${whiteListGroup.name} added`})
      return 'success'
    } catch(err) {
      console.log("Error inserting whitelist group", err);
      return 'error'
    }
  }

  async updateWhiteListGroup(whiteListGroup: any,groupId:string){
    try {
      const {data,error} = await this.supabaseService.client
      .from('whitelist_groups')
      .update(whiteListGroup)
      .eq('whitelist_group_id',groupId)
      if(error) throw error
      this.insertLog({type:'operation',title:'update whitlist group',body:`whitelist group ${whiteListGroup.name} updated`})
      return 'success'
    } catch(err) {
      console.log("Error updating whitelist group", err);
      return 'error'
    }
  }

  async getWhiteListGroupUsersCount(groupId:string){
    try {
      const {count,error} = await this.supabaseService.client
      .from('whitelist')
      .select('',{count:'exact'})
      .eq('company_id',this.companyUser?.company_id)
      .eq('whitelist_group_id',groupId)
      if(error) throw error
      //console.log(count, error);
      
      return count
    } catch(err) {
      console.log("Error fetching whitelist group users",err);
      return 'error' 
    }
  }

  async deleteWhitelistUserGroup(whiteListGroup:WhiteListGroup){
    try {
      const {data,error} = await this.supabaseService.client
      .from('whitelist_groups')
      .delete()
      .eq('whitelist_group_id', whiteListGroup.whitelist_group_id)
      .single()
      if(error) throw error
      this.insertLog({type:'operation',title:'deleted whitlist group',body:`whitelist group ${whiteListGroup.name} deleted`})
      return 'success'
    } catch(err) {
      console.log("Error deleting whitelist group", err);
      return 'error'
    }
  }

  async getCompanyVocher(){
    try {
      const {data,error} = await this.supabaseService.client
      .from('voucher')
      .select('*')
      .eq('company_id',this.companyUser?.company_id)
      .gte('ends_at',new Date().toISOString())
      .order('created_at',{ascending:false})
      if(error)  throw error
      return data
    } catch(err) {
      console.log("Error fetching voucher",err);
      return 'error'
    }
  }

  /* Whitelist Groups */

  /* Whitelist Users */

  async getWhiteList(startRange:number = 0,endRange:number = 10){
    try {
      const {count,data,error} = await this.supabaseService.client
      .from('whitelist')
      .select(`*,whitelist_groups(name)`,{count:'exact',head:false})
      .eq('company_id',this.companyUser?.company_id)
      .range(startRange,endRange)
      .order('created_at',{ascending:false})
      if(error)  throw error
      return {totalCount:count,data:data}
    } catch(err) {
      console.log("Error fetching whitelist users",err);
      return 'error'
    }
  }

  async addWhiteList(whiteListUser: any){
    try {
      const userFormWithCompanyId = {...whiteListUser, company_id:this.companyUser?.company_id}
      
      const {data,error} = await this.supabaseService.client
      .from('whitelist')
      .insert(userFormWithCompanyId)
      if(error) throw error
      this.insertLog({type:'operation',title:'added new whitlist',body:`whitelist user ${whiteListUser.email} added`})
      return 'success'
    } catch(err) {
      console.log("Error inserting whitelist user", err);
      return 'error'
    }
  }

  async updateWhiteList(whiteListUser: any){
    try {
      const userFormWithCompanyId = {...whiteListUser, company_id:this.companyUser?.company_id}

      const {data,error} = await this.supabaseService.client
      .from('whitelist')
      .update(userFormWithCompanyId)
      .eq('whitelist_id',whiteListUser.whitelist_id)
      if(error) throw error
      this.insertLog({type:'operation',title:'update whitlist user',body:`whitelist user ${whiteListUser.email} updated`})
      return 'success'
    } catch(err) {
      console.log("Error updating whitelist user", err);
      return 'error'
    }
  }

  async deleteWhitelistUser(user:any){
    try {
      const {data,error} = await this.supabaseService.client
      .from('whitelist')
      .delete()
      .eq('whitelist_id', user.whitelist_id)
      if(error) throw error
      this.insertLog({type:'operation',title:'deleted whitlist user',body:`whitelist user ${user.email} deleted`})
      return 'success'
    } catch(err) {
      console.log("Error deleting user", err);
      return 'error'
    }
  }

  /* Whitelist Users */

  /* Client Users */
  async getMyClients(pageIndex:number, pageSize:number): Promise<ClientResponse | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('get_my_clients',{company_id_param:this.companyUser?.company_id,page_index:pageIndex,page_size: pageSize})
      if(error) throw error
      this.allClients = data;
      return data
    } catch(err){
      console.log("Error estending provider vehicle", err);
      return 'error'
    }
  }
  /* Client Users */

  /* Company Stripe Section */

  async getCompanyBalance(){
    try {
      if(!this.companyStripeData?.account_id) throw 'No Stripe account found'
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke(`dashApi/stripe/balance?accountId=${this.companyStripeData?.account_id}`)
      if(error) throw error
      return data.data;
    } catch(err) {
      console.log("Error fetching account balance",err);
      return 'error'
    }
  }

  async getCompanyTransfers(limit:number,startingTransferId?:string){
    try {
      if(!this.companyStripeData?.account_id) throw 'No Stripe account found'
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke('dashApi/stripe/transfers',{
        body:{
          accountId: this.companyStripeData?.account_id,
          limit:limit,
          startingAfterTransferId: startingTransferId
        }
      })
      if(error || data.error) throw error
      return data.data;
    } catch(err) {
      console.log("Error fetching transfers for account",err);
      return 'error'
    }
  }

  async getCompanyPayouts(limit:number){
    try {
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke('dashApi/stripe/payouts',{
        body:{
          accountId: this.companyStripeData?.account_id,
          limit:limit,
        }
      })
      if(error || data.error) throw error
      return data.data;
    } catch(err) {
      console.log("Error fetching payouts for account",err);
      return 'error'
    }
  }

  async getPayoutTiles(){
    try {
      const {data,error} = await this.supabaseService.client
      .functions
      .invoke('dashApi/stripe/payout_tiles',{
        body:{
          accountId:this.companyStripeData?.account_id
        }
      })
      if(error) throw error
      return data.data
    } catch(err) {
      console.log("Error fetching payout tiles",err);
      return 'error'
    }
  }

  /* Company Stripe Section */

  /* Logs Section */

  async insertLog(logData:Logs){
    try{
      let newLog = {...logData,company_id: this.companyUser?.company_id,created_by: this.currentUser?.user_id}
      const {data,error} = await this.supabaseService.client
      .from('logs')
      .insert(newLog)
      //console.log(data, error);
    } catch(error){
      console.log("Error inserting log", error);
    }
  }

  async getCompanyLogs(rangeObject:{start:number,end:number}){
    try {
      const {count, data, error} = await this.supabaseService.client
      .from('logs')
      .select(
        `
        created_at, type, title, body,
        user!inner(first_name, last_name)
        `,{count:'exact',head:false}
      )
      .eq('company_id',this.companyUser?.company_id)
      .order('created_at',{ascending:false})
      .range(rangeObject.start,rangeObject.end)
      if(error) throw error
      //console.log(count,data,error);
      
      let mappedLogs: CompanyLogs[] = [];
      data.forEach(log => {
        if(Array.isArray(log.user)) mappedLogs.push({...log,user: log.user[0]})
        else mappedLogs.push({...log,user:log.user})
      })
      if(count) this.companyLogs.next({count:count,logs:mappedLogs})
    } catch(err){
      console.log("Error fetching logs",err);
      this.companyLogs.next({count:0,logs:[]})
    }
  }

  async getTopSixLogs(){
    try {
      const {data, error} = await this.supabaseService.client
      .from('logs')
      .select(
        `
        created_at, type, title, body,
        user!inner(first_name, last_name)
        `
      )
      .eq('company_id',this.companyUser?.company_id)
      .order('created_at',{ascending:false})
      .range(0,6)
      if(error) throw error
      let mappedLogs: CompanyLogs[] = [];
      data.forEach(log => {
        if(Array.isArray(log.user)) mappedLogs.push({...log,user: log.user[0]})
        else mappedLogs.push({...log,user:log.user})
      })
      this.topSixLogs.next(mappedLogs)
    } catch(err){
      console.log("Error fetching logs",err);
    }
  }

  /* Logs Section */


  /* Devices Section */
  
  async getDeviceModels(deviceType:string):Promise<DeviceTypeModel[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from(deviceType)
      .select('*')
      if(error) throw error
      return data
    } catch(err) {
      console.log("Error fetching device models",err);
      return []
    }
  }

  async getCompanyAssociatedDevicesCount():Promise<number>{
    try {
      const {count,error} = await this.supabaseService.client
      .from('devices')
      .select('*',{count:'exact',head:true})
      .eq('company_id',this.companyUser?.company_id)
      if(error || !count) throw error
      return count
    } catch(err) {
      console.log("Error geting associated device count",err);
      return 0
    }
  }

  async getCompanyAssociatedDevices(rangeObject:{start:number,end:number}):Promise<Devices[]>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('devices')
      .select(`
        *,
        device_data(device_id,main_battery,status,gsm_signal,battery,total_odometer,ignition,movement,network_type,guard,iccid,
         vehicle(vehicle_name)
         )
        `)
      .eq('company_id', this.companyUser?.company_id)
      .order('created_at',{ascending:false})
      .range(rangeObject.start,rangeObject.end)
      if(error) throw error
      console.log(data);
      
      this.devicesSubject.next(data)
      return data
    } catch(err){
      console.log("Error fetching company devices",err);
      this.devicesSubject.next([])
      return []
    }
  }

  async addNewDevice(deviceData:{device_type:string,model_id:string,imei:string[]}):Promise<'success' | 'error'>{
    try {
      //Construct array[] of devices with comapny_id
      const devices = deviceData.imei.map(imei => {
        return {imei:imei,company_id:this.companyUser?.company_id,device_type:deviceData.device_type,model_id: deviceData.model_id}
      })
      const {data:deviceSuccess,error:deviceError} = await this.supabaseService.client
      .from('devices')
      .insert(devices)
      if(deviceError) throw deviceError

      //Construct device_data with device_type
      const bulkDeviceData = deviceData.imei.map(imei => {
        return {imei:imei}
      })
      const {data:deviceDataSuccess, error:deviceDataError} = await this.supabaseService.client
      .from('device_data')
      .insert(bulkDeviceData)
      if(deviceDataError) throw deviceDataError
      
      this.getCompanyAssociatedDevices({start:0,end:9})
      //Insert log
      this.insertLog({type:'operation', title:'new devices added', body:`added new "${deviceData.device_type}" devices`})
      //Return success after both the things have inserted
      return 'success'
    } catch(err) {
      console.log("Error adding new device",err);
      return 'error'
    }
  }

  async getVehicleUsedWithDevice(imei:string):Promise<VehiclesName[] | 'error'> {
    try {
      const {data,error} = await this.supabaseService.client
      .from('device_data')
      .select(`vehicle(vehicle_name)`)
      .eq('imei',imei)
      if(error) throw error
      //console.log(data);
      
      return data.map((eachVehicle: { vehicle: { vehicle_name: string } | { vehicle_name: string }[] }) => {
        const vehicle = eachVehicle.vehicle as { vehicle_name: string }; // force to single object
        return { vehicle: { vehicle_name: vehicle.vehicle_name } };
      });
    } catch(err){
      console.log("Error fetching vehicle using device(imei)",err);
      return 'error'
    }
  }

  async deleteCompanyAssociatedDevice(imei:string):Promise<'success' | 'error'>{
    try {
      //Delete Device Data first
      const {data:deviceDataRes, error:deviceDataError} = await this.supabaseService.client
      .from('devices')
      .delete()
      .eq('imei',imei)
      .single()
      if(deviceDataError) throw deviceDataError

      //Delete Devices second **** Used Cascad relation to device data to delete automatically
/*       const {data:deviceRes, error:deviceError} = await this.supabaseService.client
      .from('devices')
      .delete()
      .eq('imei',imei)
      .single()
      if(deviceError) throw deviceError  */

      this.insertLog({type:'operation',title:'device deleted',body:`device ${imei} deleted`})
      //If Both goes well return success
      return 'success'
    } catch(err){
      console.log("Error deleting assocciated device",err);
      return 'error'
    }
  }

  async updateDeviceMac(imei:string,macAddress:string):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('devices')
      .update({mac_address:macAddress})
      .eq('imei',imei)
      if(error) throw error
      this.insertLog({type:'operation',title:'device MAC updated',body:`device ${imei} MAC updated`})
      return 'success'
    } catch(err){
      console.log("Error updating mac address",err);
      return 'error'
    }
  }

  /* Devices Section */



  /* Commission Section */
  async insertCommissionRule(commissionData: CommissionForm):Promise<'success' | 'error'> {
    try {
      const updatedCommision = {...commissionData,company_id:this.companyUser?.company_id}
      const {data, error} = await this.supabaseService.client
      .from('commission_rules')
      .insert(updatedCommision)
      if(error) throw error
      this.insertLog({type:'operation',title:'new commission rule',body:`commission ${commissionData.rule_name} created`})
      return 'success'
    } catch(err){
      console.log("Error inserting new commission",err);
      return 'error'
    }
  }

  async updateCommissionRule(commissionData: {rule_name:string, rules:any[]},commission_rule_id:string):Promise<'success' | 'error'>{
    try {
      const {data, error} = await this.supabaseService.client
      .from('commission_rules')
      .update(commissionData)
      .eq('commission_rule_id',commission_rule_id)
      if(error) throw error
      this.insertLog({type:'operation',title:'edited commission rule',body:`commission ${commissionData.rule_name} changed`})
      return 'success'
    } catch(error) {
      console.log("Error updating commission",error);
      return 'error'
    } 
  }

  //Used while deleting commission
  async getVehiclesUsingCommission(commissionId:string):Promise<VehicleUsingCommission[] | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('vehicle_provider')
      .select(`vehicle(vehicle_name)`)
      .eq('commission_id',commissionId)
      .eq('status','active')
      if(error) throw error
      
      return data.map((eachVehicle: { vehicle: { vehicle_name: string } | { vehicle_name: string }[] }) => {
        const vehicle = eachVehicle.vehicle as { vehicle_name: string }; // force to single object
        return { vehicle: { vehicle_name: vehicle.vehicle_name } };
      });
    } catch (error) {
      console.log("Error fetching vehicles using commission",error);
      return 'error'
    }
  }

  getCommissionData(commissionId:string): Observable<any>{
    return from(
      this.supabaseService.client
      .from('commission_rules')
      .select('*')
      .eq('commission_rule_id',commissionId)
      .single()
    ).pipe(
      map(result => {
        if(result.error) return 'error'
        return result.data.rules.filter((rule:any) => rule.company_id === this.companyUser?.company_id)[0]
      })
    )
  }
  
  async deleteCommission(commissionRuleId:string):Promise<'success' | 'error'>{
    try {
      const {error} = await this.supabaseService.client
      .from('commission_rules')
      .delete()
      .eq('commission_rule_id',commissionRuleId)
      if(error) throw error
      return 'success'
    } catch(error) {
      console.log("Error deleting commission", error);
      return 'error'
    }
  }
  /* Commission Section */

  /* Support Section */

  async submitSupportData(formData:{issue:string,details:string}):Promise<'success' | 'error'>{
    try {
      const supportData = {...formData,company_id:this.companyUser?.company_id,user_id:this.companyUser?.user_id}
      const {data,error} = await this.supabaseService.client
      .from('support_requests')
      .insert(supportData)
      if(error) throw error
      return 'success'
    } catch(err) {
      console.log("Error submitting support request form",err);
      return 'error'
    }
  }

  /* Support Section */

  /*   Account Section */

  async getCompanyUsers():Promise<CompanyUsers[] | 'error'> {
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_user')
      .select(`*,user(first_name,last_name,email,profile_picture,phone)`)
      .eq('company_id',this.companyUser?.company_id)
      .neq('type','admin')
      .order('created_at',{ascending:false})
      if(error) throw error
      return data
    } catch(err) {
      console.log("Error fetching company users", err);
      return 'error'
    }
  }

  async getUserList(searchText:string):Promise<any[]>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('user')
      .select(`
        first_name,last_name,email,user_id,
        company_user(user_id,company_id)
        `)
      .eq('company_user.company_id',this.companyUser?.company_id)//Gives if there is company user already exists in array to be used in search list to show existing users
      .neq('user_id',this.companyUser?.user_id)
      .or(`first_name.ilike.%${searchText}%,last_name.ilike.%${searchText}%,email.ilike.%${searchText}%`)
      .limit(5)
      if(error) throw error
      return data
    } catch(err) {
      console.log("Error fetching company users", err);
      return []
    }
  }

  async addCompanyUser(newCompanyUser:any):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_user')
      .insert({status:newCompanyUser.status,type:newCompanyUser.type,user_id:newCompanyUser.user_id,company_id:this.companyUser?.company_id})
      if(error) throw error
      this.insertLog({type:'operation',title:`added new ${newCompanyUser.type}`,body:`"${newCompanyUser.user_name}" added as ${newCompanyUser.type}`}) 
      return 'success'
    } catch(err) {
      console.log("Error adding new company user", err);
      return 'error'
    }
  }

  async updateCompanyUser(changedCompanyuser:any):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_user')
      .update({status:changedCompanyuser.status,type:changedCompanyuser.type})
      .eq('company_id',this.companyUser?.company_id)
      .eq('user_id',changedCompanyuser.user_id)
      if(error) throw error
      this.insertLog({type:'operation',title:`changes made for ${changedCompanyuser.user_name}`,body:`"changes are made to company user status: ${changedCompanyuser.status}" type: ${changedCompanyuser.type}`}) 
      return 'success'
    } catch(err) {
      console.log("Error updating company user", err);
      return 'error'
    }
  }

  async deleteCompanyUser(companyUser:CompanyUsers):Promise<'success' | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .from('company_user')
      .delete()
      .eq('company_user_id',companyUser.company_user_id)
      .eq('company_id',this.companyUser?.company_id)
      if(error) throw error
      this.insertLog({type:'operation',title:`deleted ${companyUser.type}`,body:`deleted ${companyUser.type} "${companyUser.user.first_name + ' ' + companyUser.user.last_name}"`})
      return 'success'
    } catch(err) {
      console.log("Error deleting company user", err);
      return 'error'
    }
  }

  clearCompanyDataForSwitch() {
    this.companyData = undefined;
    this.companyUser = undefined;
    this.companyStops = undefined;
    this.companyStripeData = undefined;
    this.stripeAccountData = undefined;
  }

  /*   Account Section */

  /*   Client Section */
  async getClientCommissionVehicles(clientId:string, commissionId:string, pageIndex:number, pageSize:number):Promise<ClientCommissionVehicleResponse | 'error'>{
    try {
      const {data,error} = await this.supabaseService.client
      .rpc('get_client_commission_vehicles',{client_id_param:clientId,commission_id_param:commissionId,page_index:pageIndex, page_size:pageSize})
      if(error) throw error
      return data
    } catch(err) {
      console.log("Failed fetching client vehicles",err);
      return 'error'
    }
  }

  async getClientCommissionTrips(commissionId:string, month:number, year:number,pageIndex:number, pageSize:number){
    try {
      const {data, error} = await this.supabaseService.client
      .rpc('get_client_trips',{commission_id_param:commissionId,month_param:month,year_param:year,page_index:pageIndex,page_size:pageSize})
      if(error) throw error
      return data
    } catch(err){
      console.log("Error retriving client commission vehicle",err);
      return 'error'
    }
  }

  /*   Client Section */

}